import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { CollectionHelper } from 'src/app/helpers/collection-helper';
import ProductHelper from 'src/app/helpers/product-helper';
import {
  CUSTOM_ADJUSTMENT_STICKER_PROPERTY,
  IPBoardHelper,
  STOCK_ADJUSTMENT_STICKER_PROPERTY,
} from 'src/app/helpers/ip-board-helper';
import { ClientBaseProjectPriceSummaryHelper } from './helper/client-base-project-price-summary-helper';
import Board from 'src/app/model/product/board/board';
import { ProductTypes } from 'src/app/model/product/product-type';
import { BaseProject } from 'src/app/model/project/base-project';
import { Project } from 'src/app/model/project/project';
import {
  IPBoardConfigurationChangedEvent,
  ProjectDisplayObservableService,
  SignSelectedEvent,
} from 'src/app/services/project-display-observable.service';
import { SubscriptionWithDestroyTriggerComponent } from 'src/app/subscription-helpers/subscription-with-destroy-trigger.component';
import { SignPlacementPriceCalculationRequestService } from 'src/app/services/sign-placement-price/sign-placement-price-calculation-request.service';
import { ClientIPBoardFormHelper } from 'src/app/shared/project-components/client-project-form/base-project/client-ip-board-form/helper/client-ip-board-form-helper';
import IPBoard from 'src/app/model/product/ip-board/ip-board';
import { Observable, Subject, Subscription } from 'rxjs';
import IPBoardPart from 'src/app/model/product/ip-board/ip-board-part';
import { debounceTime, startWith } from 'rxjs/operators';
import { PriceLine } from 'src/app/model/price-line';
import PlacementInvoice from 'src/app/model/invoice/placement-invoice';
import { GeneralHelper } from 'src/app/helpers/general-helper';
import ProjectHelper from 'src/app/helpers/project-helper';
import { BoardTypeChoices } from 'src/app/model/product/board/board-part';
import { ClientCompanySelectService } from 'src/app/services/client-company-select.service';
import Company from 'src/app/model/company/company';
import { StringHelper } from 'src/app/helpers/string-helper';
import { IP_BOARD_PART_TYPE_CHOICES } from 'src/app/model/product/ip-board/ip-board-part-type';
import { SignTypesChoices } from 'src/app/model/sign/enums/sign-type';
import { CLIENT_WINDOW_STICKER_TYPE } from 'src/app/model/product/windowsticker/client-windowsticker';
import { CLIENT_STICKER_PART_TYPE } from 'src/app/model/product/windowsticker/client-sticker-part';
import { SignAssociation } from 'src/app/components/stock/sign-associations/edit-association/sign-association';
import { SignShapeTypesChoices } from 'src/app/model/sign/enums/sign-shape-type';
import { KeysOfType } from 'src/app/helpers/utility-functions';
import { SignTitleAdjustment } from 'src/app/model/sign/title/sign-title-adjustment';
import { SignPhoneNumberAdjustment } from 'src/app/model/sign/phone/sign-phone-number-adjustment';
import { SignStickerDetail } from 'src/app/model/sign/sticker/sign-sticker-detail';

@Component({
  selector: 'client-base-project-price-summary',
  templateUrl: './client-base-project-price-summary.component.html',
  styleUrls: ['./client-base-project-price-summary.component.scss'],
})
export class ClientBaseProjectPriceSummaryComponent
  extends SubscriptionWithDestroyTriggerComponent
  implements OnInit, OnDestroy
{
  @Input()
  project: Project;
  @Input()
  baseProject: BaseProject;
  @Input()
  company: Company;
  @Input()
  client: boolean = false;

  createdDate: Date;

  atLeastOneNonStandardPrice: boolean = false;

  calculateAllSignPlacementPrices = new Subject<boolean>();
  calculateSignPlacementPriceTrigger: Observable<any>;
  calculateSignPlacementPriceSubscriptions: Subscription[] = [];

  divergentPricesOnly: boolean = false;
  toShowPriceLines: PriceLine[];
  numDistinctCompanies: number = 1;
  distinctCompanyFactor: number = 1;
  collaborativeCompanyNames: string;

  ipBoardPlacementPrice: number;
  ipBoardProductionPrice: number;
  ipBoardTextPrice: number;
  ipBoardOtherSideTextPrice: number;
  ipBoardOneSideStockTitlePrice: number;
  ipBoardOtherSideStockTitlePrice: number;
  ipBoardCustomTitlePrice: number;
  ipBoardOneSideStockPhoneNumberPrice: number;
  ipBoardOtherSideStockPhoneNumberPrice: number;
  ipBoardCustomPhoneNumberPrice: number;
  ipBoardStockStickerPrice: number;
  ipBoardOtherSideStockStickerPrice: number;
  ipBoardCustomStickerPrice: number;
  ipBoardExtraBoardPrice: number;
  ipBoardTotalPrice: number;

  secondBoardPlacementDiscount: number;

  SIGN_TYPE_CHOICES = SignTypesChoices;
  SIGN_SHAPE_TYPE_CHOICES = SignShapeTypesChoices;
  PRODUCT_TYPES = ProductTypes;
  CLIENT_WINDOW_STICKER_TYPE = CLIENT_WINDOW_STICKER_TYPE;
  CLIENT_STICKER_PART_TYPE = CLIENT_STICKER_PART_TYPE;
  IP_BOARD_PART_TYPE_CHOICES = IP_BOARD_PART_TYPE_CHOICES;

  selectedCompany: Company;

  constructor(
    protected projectDisplayObservable: ProjectDisplayObservableService,
    protected signPlacementPriceCalculationRequestService: SignPlacementPriceCalculationRequestService,
    protected clientCompanySelectService: ClientCompanySelectService
  ) {
    super();
  }

  ngOnInit(): void {
    if (this.client && this.baseProject.id != null) {
      this.selectedCompany =
        this.clientCompanySelectService.getSelectedCompany();
      if (this.selectedCompany == null) {
        throw new Error('Geen bedrijf geselecteerd');
      }
    } else if (this.project.projectContacts.length > 0) {
      this.selectedCompany = this.project.projectContacts[0].company;
    }

    this.createdDate =
      this.project.createdAt != null ? this.project.createdAt : new Date();

    this.calculateSignPlacementPriceTrigger = this.createObservableUntilDestroy(
      this.calculateAllSignPlacementPrices,
      this.projectDisplayObservable.getIPBoardConfigurationChangedSubscribable(),
      this.projectDisplayObservable.getSignSelectedSubscribable()
    );

    this.calculateBoardsAtLeastOneStandardPart();
    this.calculateAtLeastOneNonStandardPrice();
    this.calculateInvoicePreviewInfo();
    this.calculateIPBoardProductionPrices();
    this.calculateDistinctCompanyFactor();

    this.initializeSubscriptions();
    this.instantiateSignPlacementPriceSubscriptions();
    this.calculateAllSignPlacementPrices.next(true);
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    if (this.calculateAllSignPlacementPrices != null) {
      this.calculateAllSignPlacementPrices.complete();
    }
    this.unsubscribeAndUnassignSignPlacementPriceSubscriptions();
  }

  protected unsubscribeAndUnassignSignPlacementPriceSubscriptions() {
    if (
      CollectionHelper.isPopulated(
        this.calculateSignPlacementPriceSubscriptions
      )
    ) {
      this.calculateSignPlacementPriceSubscriptions.forEach((s) =>
        s.unsubscribe()
      );
      this.calculateSignPlacementPriceSubscriptions.length = 0;
    }
  }

  protected initializeSubscriptions() {
    const nonStandardPriceObservable = this.createObservableUntilDestroy(
      this.projectDisplayObservable.getProductAddedSubscribable(),
      this.projectDisplayObservable.getProductSizeChangedSubscribable(),
      this.projectDisplayObservable.getProductPartTypeChangedSubscribable(),
      this.projectDisplayObservable.getProductMaterialChangedSubscribable(),
      this.projectDisplayObservable.getStandardStickerPartsChangedSubscribable(),
      this.projectDisplayObservable.getIPBoardConfigurationChangedSubscribable(),
      this.projectDisplayObservable.getProductPlacementVariableChangedSubscribable(),
      this.projectDisplayObservable.getProjectPlacementVariableChangedSubscribable()
    );
    const calculateDistinctCompanyFactorObservable =
      this.createObservableUntilDestroy(
        this.projectDisplayObservable.getProjectContactsChangedSubscribable(),
        this.projectDisplayObservable.getProjectExtraCompanyContactsChangedSubscribable()
      );
    const reinstantiateSignPlacementPriceSubscriptionsObservable =
      this.createObservableUntilDestroy(
        this.projectDisplayObservable.getProjectContactsChangedSubscribable(),
        this.projectDisplayObservable.getProductAddedSubscribable()
      );
    const productionPricesObservable = this.createObservableUntilDestroy(
      this.projectDisplayObservable.getIPBoardConfigurationChangedSubscribable(),
      this.projectDisplayObservable.getIPBoardTextFilledSubscribable()
    );

    this.addSubscription(
      this.projectDisplayObservable.getProductSizeChangedSubscribable(),
      () => {
        this.calculateBoardsAtLeastOneStandardPart();
      }
    );

    this.addSubscription(nonStandardPriceObservable, () => {
      this.calculateAtLeastOneNonStandardPrice();
    });

    this.addSubscription(
      this.projectDisplayObservable.getProjectInvoicePriceChangedSubscribable(),
      () => {
        this.calculateInvoicePreviewInfo();
      },
      () => {},
      500
    );

    this.addSubscription(productionPricesObservable, () => {
      this.calculateIPBoardProductionPrices();
    });

    this.addSubscription(calculateDistinctCompanyFactorObservable, () => {
      this.calculateDistinctCompanyFactor();
    });

    this.addSubscription(
      reinstantiateSignPlacementPriceSubscriptionsObservable,
      () => {
        this.unsubscribeAndUnassignSignPlacementPriceSubscriptions();
        this.instantiateSignPlacementPriceSubscriptions();
        this.calculateAllSignPlacementPrices.next(true);
      }
    );
  }

  protected instantiateSignPlacementPriceSubscriptions(): void {
    if (!CollectionHelper.isPopulated(this.project.products)) {
      return;
    }
    this.calculateSignPlacementPriceSubscriptions = this.project.products
      .filter((p) => ProductHelper.isIPBoard(p))
      .map((p) => this.instantiatePlacementPriceSubscription(<IPBoard>p));
  }

  protected instantiatePlacementPriceSubscription(
    ipBoard: IPBoard
  ): Subscription {
    const observable = this.calculateSignPlacementPriceTrigger.pipe(
      startWith(false),
      debounceTime(500)
    );
    return observable.subscribe(
      (
        event: boolean | IPBoardConfigurationChangedEvent | SignSelectedEvent
      ) => {
        if (event === true || event === false) {
          this.calculateIPBoardPlacementPrice(ipBoard, null, event);
        } else {
          this.calculateIPBoardPlacementPrice(ipBoard, event, false);
        }
      }
    );
  }

  protected calculateBoardsAtLeastOneStandardPart(): void {
    if (CollectionHelper.isPopulated(this.project.products)) {
      this.project.products.forEach((p) => {
        if (ProductHelper.isBoard(p)) {
          const board = <Board>p;
          board.atLeastOneStandardPart =
            CollectionHelper.isPopulated(board.boardParts) &&
            board.boardParts.some((bp) => bp.signSize != null);
        }
      });
    }
  }

  protected calculateAtLeastOneNonStandardPrice(): void {
    this.atLeastOneNonStandardPrice =
      ClientBaseProjectPriceSummaryHelper.atLeastOneNonStandardPrice(
        this.project.products,
        this.baseProject.projectPlacement
      );
  }

  protected calculateInvoicePreviewInfo(): void {
    if (!CollectionHelper.isPopulated(this.baseProject.projectInvoices)) {
      this.divergentPricesOnly = false;
      this.toShowPriceLines = [];
      return;
    }
    const placementInvoice = this.getPlacementInvoice();
    if (placementInvoice == null) {
      return;
    }
    this.divergentPricesOnly = placementInvoice.onlyChargePriceLines;
    const toShowPriceLines: PriceLine[] = [];
    if (CollectionHelper.isPopulated(placementInvoice.priceLines)) {
      const toPush = placementInvoice.priceLines.filter((pl) => {
        if (pl.price == null) {
          return false;
        }
        const priceNum = Number(pl.price);
        return priceNum !== 0 && GeneralHelper.isNumeric(priceNum);
      });
      toShowPriceLines.push(...toPush);
    }
    this.toShowPriceLines = toShowPriceLines;
  }

  protected getPlacementInvoice(): PlacementInvoice {
    return <PlacementInvoice>(
      this.baseProject.projectInvoices.find(
        (pi) => pi.company.id === this.company.id
      )
    );
  }

  protected calculateIPBoardPlacementPrice(
    ipBoard: IPBoard,
    ipBoardConfigurationChangedEvent?:
      | IPBoardConfigurationChangedEvent
      | SignSelectedEvent,
    calculateAll = false
  ): void {
    if (
      !CollectionHelper.isPopulated(this.project.projectContacts) ||
      this.project.projectContacts[0].company == null
    ) {
      return;
    }
    if (this.project.collaborative) {
      this.ipBoardPlacementPrice = null;
      return;
    }
    if (
      calculateAll ||
      (ipBoardConfigurationChangedEvent != null &&
        ClientIPBoardFormHelper.matchesEvent(
          ipBoard.boardPart,
          ipBoardConfigurationChangedEvent
        ))
    ) {
      this.signPlacementPriceCalculationRequestService
        .calculatePlacementPriceForBoardPart(ipBoard.boardPart, this.company.id)
        .then((res: number) => {
          this.ipBoardPlacementPrice = res;
        });
    }
  }

  protected calculateDistinctCompanyFactor(): void {
    if (!this.project.collaborative) {
      this.distinctCompanyFactor = 1;
      this.numDistinctCompanies = 1;
      this.collaborativeCompanyNames = null;
      return;
    }
    let distinctCompanies = ProjectHelper.getInvolvedCompanies(this.project);
    let numDistinctCompanies =
      distinctCompanies == null ? 0 : distinctCompanies.length;
    if (numDistinctCompanies > 1) {
      this.distinctCompanyFactor = 1 / numDistinctCompanies;
      this.numDistinctCompanies = numDistinctCompanies;
      this.calculateCollaborativeCompanyNames(distinctCompanies);
      return;
    }
    numDistinctCompanies += CollectionHelper.isPopulated(
      this.project.projectExtraCompanyContacts
    )
      ? this.project.projectExtraCompanyContacts.length
      : 0;
    this.numDistinctCompanies = Math.max(numDistinctCompanies, 2); // assume two distinct companies by default for collaborative project
    this.distinctCompanyFactor = 1 / this.numDistinctCompanies;
    this.calculateCollaborativeCompanyNames(distinctCompanies);
  }

  protected calculateCollaborativeCompanyNames(companies: Company[]): void {
    const names = StringHelper.join(
      companies.map((c) => c.regularName),
      ' + '
    );
    this.collaborativeCompanyNames =
      companies.length <= 1 ? names + ' + ...' : names;
  }

  public calculateIPBoardProductionPrices(): void {
    if (
      !CollectionHelper.isPopulated(this.project.products) ||
      this.project.collaborative
    ) {
      this.nullAllIPBoardProductionPrices();
      return;
    }
    const ipBoard: IPBoard = <IPBoard>(
      this.project.products.find((p) => ProductHelper.isIPBoard(p))
    );
    if (ipBoard == null || ipBoard.boardPart == null) {
      this.nullAllIPBoardProductionPrices();
      return;
    }

    this.doCalculateIPBoardProductionPrices(ipBoard, ipBoard.boardPart);
  }

  public nullAllIPBoardProductionPrices(): void {
    this.ipBoardProductionPrice = null;
    this.ipBoardPlacementPrice = null;
    this.ipBoardTextPrice = null;
    this.ipBoardOtherSideTextPrice = null;
    this.ipBoardOneSideStockTitlePrice = null;
    this.ipBoardOtherSideStockTitlePrice = null;
    this.ipBoardCustomTitlePrice = null;
    this.ipBoardOneSideStockPhoneNumberPrice = null;
    this.ipBoardOtherSideStockPhoneNumberPrice = null;
    this.ipBoardCustomPhoneNumberPrice = null;
    this.ipBoardStockStickerPrice = null;
    this.ipBoardOtherSideStockStickerPrice = null;
    this.ipBoardCustomStickerPrice = null;
    this.ipBoardExtraBoardPrice = null;
    this.ipBoardTotalPrice = null;
    this.secondBoardPlacementDiscount = null;
  }

  protected doCalculateIPBoardProductionPrices(
    ipBoard: IPBoard,
    ipBoardPart: IPBoardPart
  ): void {
    const chargeTwice =
      ipBoardPart.boardType != null &&
      (ipBoardPart.boardType.ordinal === BoardTypeChoices.V.ordinal ||
        ipBoardPart.boardType.ordinal ===
          BoardTypeChoices.DOUBLE_SIDED.ordinal) &&
      !ipBoard.leftRightBoard;
    this.ipBoardProductionPrice = IPBoardHelper.getCustomProductionPrice(
      ipBoard,
      this.project.collaborative,
      chargeTwice
    );

    [this.ipBoardTextPrice, this.ipBoardOtherSideTextPrice] =
      this.calculateTextPrices(ipBoard, chargeTwice);

    [
      this.ipBoardOneSideStockTitlePrice,
      this.ipBoardOtherSideStockTitlePrice,
      this.ipBoardCustomTitlePrice,
    ] = this.calculateAdjustmentStickerPrices<SignTitleAdjustment>(
      ipBoard,
      chargeTwice,
      'titleAdjustment',
      'title',
      'otherTitle',
      STOCK_ADJUSTMENT_STICKER_PROPERTY.TITLE_FROM_STOCK,
      STOCK_ADJUSTMENT_STICKER_PROPERTY.OTHER_SIDE_TITLE_FROM_STOCK,
      CUSTOM_ADJUSTMENT_STICKER_PROPERTY.TITLE_FROM_STOCK,
      CUSTOM_ADJUSTMENT_STICKER_PROPERTY.OTHER_TITLE,
      CUSTOM_ADJUSTMENT_STICKER_PROPERTY.OTHER_SIDE_TITLE_FROM_STOCK,
      CUSTOM_ADJUSTMENT_STICKER_PROPERTY.OTHER_SIDE_OTHER_TITLE
    );
    [
      this.ipBoardOneSideStockPhoneNumberPrice,
      this.ipBoardOtherSideStockPhoneNumberPrice,
      this.ipBoardCustomPhoneNumberPrice,
    ] = this.calculateAdjustmentStickerPrices<SignPhoneNumberAdjustment>(
      ipBoard,
      chargeTwice,
      'phoneNumberAdjustment',
      'phoneNumber',
      'otherPhoneNumber',
      STOCK_ADJUSTMENT_STICKER_PROPERTY.PHONE_NUMBER_FROM_STOCK,
      STOCK_ADJUSTMENT_STICKER_PROPERTY.OTHER_SIDE_PHONE_NUMBER_FROM_STOCK,
      CUSTOM_ADJUSTMENT_STICKER_PROPERTY.PHONE_NUMBER_FROM_STOCK,
      CUSTOM_ADJUSTMENT_STICKER_PROPERTY.OTHER_PHONE_NUMBER,
      CUSTOM_ADJUSTMENT_STICKER_PROPERTY.OTHER_SIDE_PHONE_NUMBER_FROM_STOCK,
      CUSTOM_ADJUSTMENT_STICKER_PROPERTY.OTHER_SIDE_OTHER_PHONE_NUMBER
    );
    [
      this.ipBoardStockStickerPrice,
      this.ipBoardOtherSideStockStickerPrice,
      this.ipBoardCustomStickerPrice,
    ] = this.calculateAdjustmentStickerPrices<SignStickerDetail>(
      ipBoard,
      chargeTwice,
      'sticker',
      'text',
      'otherSticker',
      STOCK_ADJUSTMENT_STICKER_PROPERTY.STICKER_FROM_STOCK,
      STOCK_ADJUSTMENT_STICKER_PROPERTY.STICKER_FROM_STOCK,
      CUSTOM_ADJUSTMENT_STICKER_PROPERTY.STICKER_FROM_STOCK,
      CUSTOM_ADJUSTMENT_STICKER_PROPERTY.OTHER_STICKER,
      CUSTOM_ADJUSTMENT_STICKER_PROPERTY.STICKER_FROM_STOCK,
      CUSTOM_ADJUSTMENT_STICKER_PROPERTY.OTHER_STICKER
    );

    this.ipBoardExtraBoardPrice = IPBoardHelper.getExtraBoardPrice(ipBoard);

    this.ipBoardTotalPrice = [
      this.ipBoardProductionPrice,
      this.ipBoardTextPrice,
      this.ipBoardOtherSideTextPrice,
      this.ipBoardOneSideStockTitlePrice,
      this.ipBoardOtherSideStockTitlePrice,
      this.ipBoardCustomTitlePrice,
      this.ipBoardOneSideStockPhoneNumberPrice,
      this.ipBoardOtherSideStockPhoneNumberPrice,
      this.ipBoardCustomPhoneNumberPrice,
      this.ipBoardStockStickerPrice,
      this.ipBoardOtherSideStockStickerPrice,
      this.ipBoardCustomStickerPrice,
      this.ipBoardExtraBoardPrice,
    ].reduce(GeneralHelper.addGuardNull);

    this.secondBoardPlacementDiscount =
      this.client && this.project.collaborative
        ? 0
        : IPBoardHelper.getSecondBoardPlacementDiscount(ipBoardPart);
  }

  private calculateTextPrices(
    ipBoard: IPBoard,
    chargeTwice: boolean
  ): number[] {
    if (!ipBoard.leftRightBoard) {
      return [
        IPBoardHelper.getTextPriceForBoardPart(ipBoard.boardPart, chargeTwice),
        null,
      ];
    }

    return ipBoard.boardPart != null &&
      ipBoard.otherSideBoardPart != null &&
      ((StringHelper.isEmpty(ipBoard.boardPart.customText) &&
        StringHelper.isEmpty(ipBoard.otherSideBoardPart.customText)) ||
        (ipBoard.boardPart.customText != null &&
          ipBoard.otherSideBoardPart.customText != null &&
          StringHelper.transform(
            ipBoard.boardPart.customText,
            String.prototype.trim,
            String.prototype.toLowerCase
          ) ===
            StringHelper.transform(
              ipBoard.otherSideBoardPart.customText,
              String.prototype.trim,
              String.prototype.toLowerCase
            )))
      ? [IPBoardHelper.getTextPrice(ipBoard, chargeTwice), null]
      : [
          IPBoardHelper.getTextPriceForBoardPart(
            ipBoard.boardPart,
            chargeTwice
          ),
          IPBoardHelper.getTextPriceForBoardPart(
            ipBoard.otherSideBoardPart,
            chargeTwice
          ),
        ];
  }

  // return[0]: oneSideStockPrice, return[1]: otherSideStockPrice, return[2]: customPrice
  private calculateAdjustmentStickerPrices<A extends SignAssociation>(
    ipBoard: IPBoard,
    chargeTwice: boolean,
    adjustmentSticker: KeysOfType<IPBoardPart, A>,
    adjustmentStickerContentProp: KeysOfType<A, string>,
    customProp: KeysOfType<IPBoardPart, string>,
    oneSideFromStockProperty: STOCK_ADJUSTMENT_STICKER_PROPERTY,
    otherSideFromStockProperty: STOCK_ADJUSTMENT_STICKER_PROPERTY,
    customAdjustmentStickerOneSideFromStockProperty: CUSTOM_ADJUSTMENT_STICKER_PROPERTY,
    customAdjustmentStickerOneSideCustomProperty: CUSTOM_ADJUSTMENT_STICKER_PROPERTY,
    customAdjustmentStickerOtherSideFromStockProperty: CUSTOM_ADJUSTMENT_STICKER_PROPERTY,
    customAdjustmentStickerOtherSideCustomProperty: CUSTOM_ADJUSTMENT_STICKER_PROPERTY
  ): number[] {
    if (!ipBoard.leftRightBoard) {
      return this.calculateNonLeftRightAdjustmentStickerPrices(
        ipBoard.boardPart,
        chargeTwice,
        oneSideFromStockProperty,
        otherSideFromStockProperty,
        customAdjustmentStickerOneSideFromStockProperty,
        customAdjustmentStickerOneSideCustomProperty,
        customAdjustmentStickerOtherSideFromStockProperty,
        customAdjustmentStickerOtherSideCustomProperty
      );
    }
    return this.calculateLeftRightAdjustmentStickerPrices(
      ipBoard,
      adjustmentSticker,
      adjustmentStickerContentProp,
      customProp
    );
  }

  private calculateNonLeftRightAdjustmentStickerPrices(
    ipBoardPart: IPBoardPart,
    chargeTwice: boolean,
    oneSideFromStockProperty: STOCK_ADJUSTMENT_STICKER_PROPERTY,
    otherSideFromStockProperty: STOCK_ADJUSTMENT_STICKER_PROPERTY,
    customAdjustmentStickerOneSideFromStockProperty: CUSTOM_ADJUSTMENT_STICKER_PROPERTY,
    customAdjustmentStickerOneSideCustomProperty: CUSTOM_ADJUSTMENT_STICKER_PROPERTY,
    customAdjustmentStickerOtherSideFromStockProperty: CUSTOM_ADJUSTMENT_STICKER_PROPERTY,
    customAdjustmentStickerOtherSideCustomProperty: CUSTOM_ADJUSTMENT_STICKER_PROPERTY
  ): number[] {
    const stockAdjustmentStickerPrices =
      this.calculateStockSideAdjustmentPrices(
        ipBoardPart,
        oneSideFromStockProperty,
        otherSideFromStockProperty,
        chargeTwice
      );
    return [
      ...stockAdjustmentStickerPrices,
      IPBoardHelper.getCustomAdjustmentStickerPriceForBoardPart(
        ipBoardPart,
        chargeTwice,
        customAdjustmentStickerOneSideFromStockProperty,
        customAdjustmentStickerOneSideCustomProperty,
        customAdjustmentStickerOtherSideFromStockProperty,
        customAdjustmentStickerOtherSideCustomProperty
      ),
      null,
    ];
  }

  private calculateLeftRightAdjustmentStickerPrices<A extends SignAssociation>(
    ipBoard: IPBoard,
    adjustmentSticker: KeysOfType<IPBoardPart, A>,
    adjustmentStickerContentProp: KeysOfType<A, string>,
    customProp: KeysOfType<IPBoardPart, string>
  ): number[] {
    if (ipBoard.boardPart == null && ipBoard.otherSideBoardPart == null) {
      return [null, null, null];
    }
    const oneSideAdjustmentSticker =
      ipBoard.boardPart == null
        ? null
        : <A>(<unknown>ipBoard.boardPart[adjustmentSticker]);
    const oneSideAdjustmentStickerContent =
      oneSideAdjustmentSticker == null
        ? null
        : <string>(
            (<unknown>oneSideAdjustmentSticker[adjustmentStickerContentProp])
          );
    const otherSideAdjustmentSticker =
      ipBoard.otherSideBoardPart == null
        ? null
        : <A>(<unknown>ipBoard.otherSideBoardPart[adjustmentSticker]);
    const otherSideAdjustmentStickerContent =
      otherSideAdjustmentSticker == null
        ? null
        : <string>(
            (<unknown>otherSideAdjustmentSticker[adjustmentStickerContentProp])
          );

    if (
      oneSideAdjustmentSticker != null &&
      otherSideAdjustmentSticker != null &&
      ipBoard.boardPart.sign != null &&
      ipBoard.otherSideBoardPart.sign != null &&
      StringHelper.transform(
        oneSideAdjustmentStickerContent,
        String.prototype.trim,
        String.prototype.toLowerCase
      ) ===
        StringHelper.transform(
          otherSideAdjustmentStickerContent,
          String.prototype.trim,
          String.prototype.toLowerCase
        )
    ) {
      const singleAdjustmentStickerPrice = [
        oneSideAdjustmentSticker.charge == null ||
        !oneSideAdjustmentSticker.charge.value ||
        ipBoard.boardPart.sign.withoutStock
          ? null
          : oneSideAdjustmentSticker.charge.price,
        otherSideAdjustmentSticker.charge == null ||
        !otherSideAdjustmentSticker.charge.value ||
        ipBoard.otherSideBoardPart.sign.withoutStock
          ? null
          : otherSideAdjustmentSticker.charge.price,
      ].reduce(GeneralHelper.addGuardNull);
      return [singleAdjustmentStickerPrice, null, null];
    }

    const customAdjustmentPrice = [
      ipBoard.boardPart == null ||
      ipBoard.boardPart.sign == null ||
      (ipBoard.boardPart.sign != null && ipBoard.boardPart.sign.withoutStock) ||
      StringHelper.isEmpty(ipBoard.boardPart[customProp])
        ? null
        : IPBoardHelper.BASE_CUSTOM_ADJUSTMENT_PRICE,
      ipBoard.otherSideBoardPart == null ||
      ipBoard.otherSideBoardPart.sign == null ||
      (ipBoard.otherSideBoardPart.sign != null &&
        ipBoard.otherSideBoardPart.sign.withoutStock) ||
      StringHelper.isEmpty(ipBoard.otherSideBoardPart[customProp])
        ? null
        : IPBoardHelper.BASE_CUSTOM_ADJUSTMENT_PRICE,
    ].reduce(GeneralHelper.addGuardNull);

    return [
      oneSideAdjustmentSticker == null ||
      oneSideAdjustmentSticker.charge == null ||
      !oneSideAdjustmentSticker.charge.value ||
      ipBoard.boardPart.sign.withoutStock
        ? null
        : oneSideAdjustmentSticker.charge.price,
      otherSideAdjustmentSticker == null ||
      otherSideAdjustmentSticker.charge == null ||
      !otherSideAdjustmentSticker.charge.value ||
      ipBoard.otherSideBoardPart.sign.withoutStock
        ? null
        : otherSideAdjustmentSticker.charge.price,
      customAdjustmentPrice,
    ];
  }

  // return[0]: oneSideStockAdjustmentPrice, return[1]: otherSideStockAdjustmentPrice
  private calculateStockSideAdjustmentPrices(
    ipBoardPart: IPBoardPart,
    oneSideProperty: STOCK_ADJUSTMENT_STICKER_PROPERTY,
    otherSideProperty: STOCK_ADJUSTMENT_STICKER_PROPERTY,
    chargeTwice: boolean
  ): number[] {
    if (ipBoardPart.sign == null || ipBoardPart.sign.withoutStock) {
      // prices do not apply if it isn't a stock sign
      return [null, null];
    }

    const oneSideFromStock: SignAssociation = <SignAssociation>(
      ipBoardPart[oneSideProperty]
    );
    const otherSideFromStock = <SignAssociation>ipBoardPart[otherSideProperty];

    if (oneSideFromStock == null && otherSideFromStock == null) {
      // prices do not apply if no stock adjustments were picked
      return [null, null];
    }

    // Due to how IPBoardAssociationPicker works, the board part will have the same adjustment for the other side property
    // if board part is not a V-board or if client did not explicitly pick a different option for the other side
    const representSameAdjustment = IPBoardHelper.representSameAdjustment(
      oneSideFromStock,
      otherSideFromStock
    );
    const oneSidePrice =
      oneSideFromStock == null ||
      oneSideFromStock.charge == null ||
      !oneSideFromStock.charge.value
        ? null
        : chargeTwice && representSameAdjustment
        ? 2 * oneSideFromStock.charge.price
        : oneSideFromStock.charge.price;
    const otherSidePrice =
      otherSideFromStock == null || representSameAdjustment
        ? null
        : otherSideFromStock.charge != null && otherSideFromStock.charge.value
        ? otherSideFromStock.charge.price
        : null;
    return [oneSidePrice, otherSidePrice];
  }
}
